import { AnyError } from '@microsoft/portal-app/lib/IAnyError';
import { UseMutationResult, useQueryClient, useMutation } from 'react-query';
import { getRegionalAndLanguageUrl } from '../../redux/modules/graphApis';
import { ITranslationPreferences } from '../models';
import { QueryKey } from '../models/QueryKey';
import { useAuth } from '@iamexperiences/feature-auth';
import { useReportAPITelemetry } from './useReportAPITelemetry';
import { HttpRequestError } from '../../shared/HttpRequestError';
import {
  IRegionalAndLanguageSettingsQueryResult,
  useGetRegionalAndLanguageSettingsQuery,
} from './useGetRegionalAndLanguageSettingQuery';
import { getMSGraphScopeAndEndPoint } from '../../shared/utils';
import { MSGraphScopes } from '../../shared/constants';

export const useSetTranslationPreferenceQuery = (): UseMutationResult<unknown, AnyError, string, unknown> => {
  const { auth } = useAuth();
  const { translationPreferences } = useGetRegionalAndLanguageSettingsQuery();
  const queryClient = useQueryClient();

  const { startTelemetryReport, stopTelemetryReport } = useReportAPITelemetry('setTranslationPreference');

  return useMutation(async (preference: string) => {
    const curTranslationPreferences: ITranslationPreferences = {
      ...translationPreferences,
      translationBehavior: preference,
    };

    const url = getRegionalAndLanguageUrl();
    startTelemetryReport({ payload: preference });
    const token = await auth.acquireToken({ scopes: [getMSGraphScopeAndEndPoint(MSGraphScopes.UserReadWrite).scope] });
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: 'Bearer ' + token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        translationPreferences: {
          translationBehavior: preference,
        },
      }),
    });

    if (!response.ok) {
      const error = new HttpRequestError(response.status, response.statusText);
      stopTelemetryReport('error', error);
      throw error;
    }

    queryClient.setQueryData<IRegionalAndLanguageSettingsQueryResult | undefined>(
      QueryKey.REGIONAL_AND_LANGUAGE_SETTINGS,
      (old) => {
        if (old) {
          return {
            ...old,
            translationPreferences: curTranslationPreferences,
          };
        }
        return old;
      },
    );

    stopTelemetryReport('success', preference);
    return response;
  });
};
